import { ChangeEvent, ReactElement, useMemo } from 'react';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/styles';
import { SearchRounded } from '@mui/icons-material';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Field, FieldProps, useField, useFormikContext } from 'formik';

const StyledAutocomplete = styled(Autocomplete)({
  '.MuiInputBase-input': {
    padding: '0 0rem !important',
  },
});

export interface ITypeaheadOption {
  id: string;
  label: string;
}

export interface ITypeaheadProps {
  className?: string;
  freeSolo?: boolean;
  id?: string;
  name: string;
  onChange?: (name: string, value: ITypeaheadOption | string) => void;
  options: ITypeaheadOption[];
  placeholder?: string;
}

const inputAdornment = (
  <InputAdornment position="start">
    <SearchRounded />
  </InputAdornment>
);

const style = { minWidth: '50%' };

export const Typeahead = ({
  className,
  freeSolo = false,
  id,
  name,
  onChange,
  options,
  placeholder = 'Select an option',
}: ITypeaheadProps): ReactElement => {
  const { setTouched, setFieldValue, touched } = useFormikContext();
  const [field] = useField(name);
  const { value } = field;

  // to make this work properly as a typeahead, we need to continue to feed the original value as `value` to Autocomplete - this captures it (and works on the first render, as opposed to useEffect)
  const initialValue = useMemo(() => value, []);

  const handleBlur = (): void => {
    setTouched({ ...touched, [name]: true });
  };

  const handleChange = (_: ChangeEvent<unknown>, value: ITypeaheadOption): void => {
    if (freeSolo) {
      // in text (aka freeSolo) mode, we need the text from the selected item
      setFieldValue(name, value?.label, true);
      onChange?.(name, value?.label);
    } else {
      setFieldValue(name, value, true);
      onChange?.(name, value);
    }
  };

  const handleInputChange = (_: ChangeEvent<unknown>, value: string): void => {
    setFieldValue(name, value || '', false);
    onChange?.(name, value);
  };

  // Returns true if an option is selected
  const getOptionSelected = (current: ITypeaheadOption, next: ITypeaheadOption): boolean => {
    return current?.id === next?.id;
  };

  // Renders the label for the field to display
  const getOptionLabel = (option: ITypeaheadOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option?.label || '';
  };

  const renderInput = (params: AutocompleteRenderInputParams): ReactElement => (
    <TextField
      {...params}
      placeholder={placeholder}
      variant="outlined"
      slotProps={{
        input: { ...params.InputProps, endAdornment: null, startAdornment: inputAdornment },
        htmlInput: { ...params.inputProps, 'aria-label': placeholder, id: id || name },
      }}
    />
  );
  const renderAutocomplete = (fieldProps: FieldProps): ReactElement => {
    return (
      <StyledAutocomplete
        {...fieldProps}
        {...field}
        aria-label={name}
        className={className}
        freeSolo={freeSolo}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        onBlur={handleBlur}
        onChange={handleChange}
        options={options}
        renderInput={renderInput}
        style={style}
        {...(freeSolo
          ? {
              inputValue: value || '',
              onInputChange: handleInputChange,
              value: initialValue, // if we use `value` here, the options don't filter as you type
            }
          : {
              value: value || null,
            })}
      />
    );
  };
  return <Field name={name}>{renderAutocomplete}</Field>;
};
